import { useRef, useEffect, useState } from 'react';
import PalmerSectionLabel from './PalmerSectionLabel';

const plans = [
  {
    price: '$99',
    period: '/Month',
    name: 'Starter Plan',
    desc: 'Perfect for small launches and personal sites that need a fast online presence.',
    features: [
      { text: 'One-page website', active: true },
      { text: 'Custom layout & visuals', active: true },
      { text: 'Mobile-first responsive build', active: true },
      { text: 'Fast delivery (within 7 days)', active: false },
      { text: 'Design system setup', active: false },
      { text: 'SEO-ready structure', active: false },
      { text: 'Basic CMS integration', active: false },
      { text: 'Contact form setup', active: false },
    ],
    inverted: false,
    popular: false,
  },
  {
    price: '$299',
    period: '/Month',
    name: 'Growth Plan',
    desc: 'Designed for growing brands that need flexibility and CMS support.',
    features: [
      { text: 'Up to 5 pages', active: true },
      { text: 'CMS-powered sections', active: true },
      { text: 'Component-based structure', active: true },
      { text: 'Motion design & transitions', active: true },
      { text: 'Clean UX-focused layout', active: true },
      { text: 'Device-optimized responsiveness', active: false },
      { text: 'Style guide system', active: false },
      { text: 'Email capture / integrations', active: false },
    ],
    inverted: false,
    popular: false,
  },
  {
    price: '$899',
    period: '/Month',
    name: 'Full Scope Plan',
    desc: 'Best for studios or teams needing structure and enterprise-level execution.',
    features: [
      { text: '10+ pages with CMS', active: true },
      { text: 'Advanced layout strategy', active: true },
      { text: 'Full brand system support', active: true },
      { text: 'Animation direction', active: true },
      { text: 'Custom-built components', active: true },
      { text: 'CMS training', active: true },
      { text: 'Launch support + QA', active: true },
      { text: 'Performance optimization', active: true },
    ],
    inverted: true,
    popular: true,
  },
];

export default function PalmerPricing() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-black py-20">
      <PalmerSectionLabel
        left1="© Project Pricing プラン"
        left2="(WDX® — 09)"
        right="Custom Quotes"
      />

      <div className="px-6">
        {/* Heading */}
        <div className="mb-10">
          <h2
            className={`text-white font-semibold transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(48px, 7.5vw, 110px)',
              letterSpacing: '-0.04em',
              lineHeight: 1,
            }}
          >
            Starter.
          </h2>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              className={`relative border p-8 flex flex-col transition-all duration-500 ${
                plan.inverted
                  ? 'bg-white border-white text-black'
                  : 'bg-transparent border-white/10 text-white'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute top-4 right-4 bg-black text-white text-xs px-2 py-0.5" style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.08em' }}>
                  Popular
                </div>
              )}

              {/* Price */}
              <div className="flex items-end gap-1 mb-4">
                <h3
                  className="font-semibold leading-none"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 'clamp(40px, 4vw, 64px)',
                    letterSpacing: '-0.03em',
                    color: plan.inverted ? 'black' : 'white',
                  }}
                >
                  {plan.price}
                </h3>
                <span
                  className="text-sm mb-1"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: plan.inverted ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.35)',
                  }}
                >
                  {plan.period}
                </span>
              </div>

              <p
                className="text-sm font-medium mb-1"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: plan.inverted ? 'black' : 'white',
                }}
              >
                {plan.name}
              </p>
              <p
                className="text-xs leading-relaxed mb-6"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: plan.inverted ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.35)',
                }}
              >
                {plan.desc}
              </p>

              <div
                className="h-px mb-6"
                style={{ background: plan.inverted ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.08)' }}
              ></div>

              {/* Features */}
              <div className="flex flex-col gap-3 flex-1">
                {plan.features.map((f) => (
                  <div key={f.text} className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: f.active
                          ? plan.inverted ? 'black' : 'white'
                          : 'transparent',
                        border: f.active
                          ? 'none'
                          : plan.inverted ? '1px solid rgba(0,0,0,0.18)' : '1px solid rgba(255,255,255,0.12)',
                      }}
                    >
                      {f.active && (
                        <i
                          className="ri-check-line text-xs"
                          style={{ color: plan.inverted ? 'white' : 'black' }}
                        ></i>
                      )}
                    </div>
                    <span
                      className="text-xs"
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        color: f.active
                          ? plan.inverted ? 'black' : 'white'
                          : plan.inverted ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.22)',
                      }}
                    >
                      {f.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="/contact"
                className="mt-8 block text-center text-xs py-3 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.1em',
                  border: plan.inverted ? '1px solid black' : '1px solid rgba(255,255,255,0.25)',
                  color: plan.inverted ? 'black' : 'white',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.background = plan.inverted ? 'black' : 'white';
                  el.style.color = plan.inverted ? 'white' : 'black';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.background = 'transparent';
                  el.style.color = plan.inverted ? 'black' : 'white';
                }}
              >
                GET STARTED
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
